import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PasswordStrengthIndicator } from "@/components/PasswordStrengthIndicator";
import { validatePasswordStrength } from "@/lib/passwordValidation";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { logger } from '@/lib/logger';

const ResetPassword = () => {
  const { updatePassword } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidSession, setIsValidSession] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);

  useEffect(() => {
    // Check if we have a valid password reset session
    const checkSession = async () => {
      try {
        // First, let Supabase handle any URL fragments automatically
        const { data: { session } } = await supabase.auth.getSession();
        
        logger.debug('Current session:', session);
        logger.debug('URL search params:', Object.fromEntries(searchParams.entries()));
        logger.debug('URL hash:', window.location.hash);
        
        if (session && session.user) {
          // We have a valid session, allow password update
          logger.debug('Valid session found:', session.user);
          setIsValidSession(true);
          setIsCheckingSession(false);
          return;
        }

        // Check URL params for recovery tokens
        const accessToken = searchParams.get('access_token');
        const refreshToken = searchParams.get('refresh_token');
        const type = searchParams.get('type');

        // Also check URL hash fragments (Supabase often uses hash)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const hashAccessToken = hashParams.get('access_token');
        const hashRefreshToken = hashParams.get('refresh_token');
        const hashType = hashParams.get('type');

        const finalAccessToken = accessToken || hashAccessToken;
        const finalRefreshToken = refreshToken || hashRefreshToken;
        const finalType = type || hashType;

        logger.debug('Reset password tokens:', {
          searchParams: { accessToken, refreshToken, type },
          hashParams: { hashAccessToken, hashRefreshToken, hashType },
          final: { finalAccessToken: !!finalAccessToken, finalRefreshToken: !!finalRefreshToken, finalType }
        });

        if (finalType === 'recovery' && finalAccessToken && finalRefreshToken) {
          logger.debug('Setting session with recovery tokens');
          const { data, error } = await supabase.auth.setSession({
            access_token: finalAccessToken,
            refresh_token: finalRefreshToken,
          });
          
          if (error) {
            logger.error('Error setting session:', error);
            toast({
              variant: "destructive",
              title: "Invalid reset link",
              description: "This password reset link is invalid or has expired. Please request a new one.",
            });
            navigate('/auth');
          } else {
            logger.debug('Session set successfully:', data);
            setIsValidSession(true);
          }
        } else {
          logger.debug('No valid recovery tokens found');
          toast({
            variant: "destructive",
            title: "Invalid access",
            description: "This page can only be accessed through a valid password reset link.",
          });
          navigate('/auth');
        }
      } catch (error) {
        logger.error('Session check error:', error);
        toast({
          variant: "destructive",
          title: "Session error",
          description: "There was an error checking your session. Please try again.",
        });
        navigate('/auth');
      }
      
      setIsCheckingSession(false);
    };

    checkSession();
  }, [searchParams, navigate, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      toast({
        variant: "destructive",
        title: "Missing fields",
        description: "Please fill in all fields.",
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        variant: "destructive",
        title: "Passwords don't match",
        description: "Please make sure both passwords match.",
      });
      return;
    }

    const passwordValidation = validatePasswordStrength(password);
    if (!passwordValidation.isValid) {
      toast({
        variant: "destructive",
        title: "Password requirements not met",
        description: "Please ensure your password meets all security requirements shown below.",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await updatePassword(password);
      toast({
        title: "Password updated successfully",
        description: "Your password has been updated. You can now sign in with your new password.",
      });
      navigate('/');
    } catch (error) {
      logger.error('Password update error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isCheckingSession) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isValidSession) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">Reset Password</CardTitle>
          <CardDescription className="text-center">
            Enter your new password below
          </CardDescription>
        </CardHeader>
        
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <Input 
                id="password" 
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your new password"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <Input 
                id="confirm-password" 
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm your new password"
              />
            </div>
            
            <PasswordStrengthIndicator 
              password={password} 
              className="mt-2"
            />
            
            {password && confirmPassword && password !== confirmPassword && (
              <Alert variant="destructive">
                <AlertDescription>
                  Passwords don't match
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
          
          <CardFooter className="flex flex-col space-y-4">
            <Button 
              type="submit" 
              className="w-full"
              disabled={isSubmitting || !validatePasswordStrength(password).isValid || password !== confirmPassword}
            >
              {isSubmitting ? "Updating password..." : "Update Password"}
            </Button>
            
            <Button 
              type="button" 
              variant="outline" 
              className="w-full"
              onClick={() => navigate('/auth')}
            >
              Back to Sign In
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default ResetPassword;